import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  TemplateRef,
  Renderer2,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { GasForm } from '../../gas-form.namespace';
import { GasRadioComponent } from '../radio/gas-radio.component';
import { GasFormBaseComponent } from '../../gas-form-base-component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'gas-radio-group',
  templateUrl: './gas-radio-group.component.html',
  styleUrls: ['./gas-radio-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GasRadioGroupComponent),
      multi: true,
    },
  ],
})
export class GasRadioGroupComponent<T = GasForm.GasFilterFormBaseValue>
  extends GasFormBaseComponent
  implements ControlValueAccessor, OnChanges, AfterContentInit {
  @ContentChildren(GasRadioComponent, { descendants: true })
  public optionRefs: QueryList<GasRadioComponent<T>>;

  @Input()
  public label: string | TemplateRef<ElementRef>;

  @Input()
  public error: string;

  @Input()
  public direction: string | 'row' | 'column' = 'column';

  public defaultValue: T;

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
    public elementRef: ElementRef,
    public renderer: Renderer2,
  ) {
    super(changeDetectorRef, elementRef, renderer);

    this.typeChanged$
      .pipe(
        untilDestroyed(this),
      )
      .subscribe((type: GasForm.InputState) => this.setInputState(type));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.optionRefs && changes.type) {
      this.setInputState(changes.type.currentValue);
    }
  }

  public ngAfterContentInit(): void {
    /** Подписка на клик дочерних компонентов GasRadioComponent. */
    this.optionRefs?.forEach((option: GasRadioComponent<T>) => {
      option.viewMode = this.state as unknown as 'list' | 'button';
      option.inputType.next(this.state);
      option.form = this.form;

      if (this.form.value) {
        option.isDefaultValue = option.value === this.form.value;
      }

      option.clicked$
        .pipe(
          untilDestroyed(this),
        )
        .subscribe(() => {
          this.form.setValue(option.value);
        });
    });
  }

  public isButton(): boolean {
    return this.state === 'button';
  }

  private setDefaultValue(input: GasRadioComponent<T>): void {
    this.defaultValue = input.value;

    input.isDefaultValue = true;
  }

  private setInputState(state: GasForm.InputState): void {
    this.optionRefs?.forEach((option: GasRadioComponent<T>) => option.state = state);

    /** Обновляем дефолтное поле, когда пришел Success тип */
    if (state === GasForm.InputState.Success) {
      const activeOption = this.optionRefs.toArray()
        .find((option: GasRadioComponent<T>) => option.isActive);

      if (activeOption) {
        this.setDefaultValue(activeOption);

        activeOption.changeDetector.detectChanges();
      }

      /** Убираем дефолтные значения со старых опций */
      this.optionRefs.forEach((option: GasRadioComponent<T>) => {
        option.isDefaultValue = option.value === this.defaultValue;

        option.changeDetector.detectChanges();
      });
    }
  }
}
